import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { Spinner } from 'components/Spinner'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import type { ProfileCubit } from '@breakoutlearning/firebase-repository/cubits/ProfileCubit'
import { isProdOrBeta } from 'config/environment'

export const ProfileLtiSettings = observer(function ProfileLtiSettings({
  cubit,
}: {
  cubit: ProfileCubit
}) {
  const breakoutUser = useBreakoutUser()
  const { tt } = useTranslationTyped()
  const isRestrictedEnv = isProdOrBeta()

  const [breakoutAppUrl, setBreakoutAppUrl] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const initializedRef = useRef(false)
  const settings = cubit.ltiSettings

  useEffect(() => {
    if (!breakoutUser.isAdmin || isRestrictedEnv) return
    cubit.startLtiSettingsStream()
  }, [breakoutUser.isAdmin, cubit, isRestrictedEnv])

  useEffect(() => {
    if (!breakoutUser.isAdmin || isRestrictedEnv) return
    if (!settings.isLoaded) return
    if (initializedRef.current) return

    setBreakoutAppUrl(settings.data?.breakoutAppUrl ?? '')
    initializedRef.current = true
  }, [
    breakoutUser.isAdmin,
    isRestrictedEnv,
    settings.isLoaded,
    settings.data?.breakoutAppUrl,
  ])

  if (!breakoutUser.isAdmin || isRestrictedEnv) return null

  const currentValue = settings.data?.breakoutAppUrl ?? ''
  const normalizedCurrent = currentValue.trim()
  const normalizedInput = breakoutAppUrl.trim()
  const hasChanges = normalizedInput !== normalizedCurrent
  const canReset = normalizedCurrent !== '' || normalizedInput !== ''

  const handleSave = async () => {
    if (!hasChanges) return
    setIsSaving(true)
    try {
      await cubit.updateBreakoutAppUrl(normalizedInput || '')
      setBreakoutAppUrl(normalizedInput)
      toast.success(tt.profile.lti_settings_saved(), {
        id: 'lti-settings-saved',
      })
    } catch (error) {
      console.error(error)
      toast.error(tt.profile.lti_settings_save_error())
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = async () => {
    if (!canReset) return
    setIsSaving(true)
    try {
      await cubit.updateBreakoutAppUrl('')
      setBreakoutAppUrl('')
      toast.success(tt.profile.lti_settings_saved(), {
        id: 'lti-settings-saved',
      })
    } catch (error) {
      console.error(error)
      toast.error(tt.profile.lti_settings_save_error())
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex min-h-full flex-col">
      <h1 className="text-headline-large">{tt.profile.lti_settings()}</h1>
      <div className="mb-3 text-body-large text-core-on-tertiary">
        {tt.profile.lti_settings_description()}
      </div>
      <div className="mt-6 flex-1 rounded-3xl bg-surface p-6">
        {settings.isLoading && !settings.hasData && (
          <div className="flex flex-1 flex-col items-center justify-center">
            <Spinner size={2} />
            <strong className="mt-2 text-body-medium text-core-on-tertiary">
              {tt.profile.lti_settings_loading()}
            </strong>
          </div>
        )}
        {!settings.isLoading && (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              void handleSave()
            }}
          >
            <div className="rounded-2xl bg-core-tertiary p-4">
              <div className="text-body-small text-on-surface-var">
                {tt.profile.lti_breakout_app_url_current()}
              </div>
              <div className="text-body-medium">
                {currentValue || tt.profile.lti_breakout_app_url_not_set()}
              </div>
            </div>
            <BreakoutTextInput
              kind="tertiary"
              label={tt.profile.lti_breakout_app_url_label()}
              sublabel={tt.profile.lti_breakout_app_url_helper()}
              value={breakoutAppUrl}
              onChange={(event) => setBreakoutAppUrl(event.target.value)}
              disabled={isSaving}
              testId="lti-breakout-app-url"
            />
            <div className="flex justify-end gap-3">
              <BreakoutButton
                size="large"
                kind="tertiary"
                type="button"
                disabled={!canReset || isSaving}
                onClick={handleReset}
              >
                {tt.common.reset()}
              </BreakoutButton>
              <BreakoutButton
                size="large"
                kind="primary"
                type="submit"
                disabled={!hasChanges || isSaving}
                loading={isSaving}
              >
                {tt.profile.save()}
              </BreakoutButton>
            </div>
          </form>
        )}
      </div>
    </div>
  )
})
